import Constants from 'Constants'
import { blobToBase64, getExceptionImageBlob, getExceptionImageBlobFromPath } from 'utils/file'

// firebasestorage.googleapis.com/.../o/<encoded-path>?alt=media → GCS object path
const FIREBASE_STORAGE_OBJECT_PATH = /firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)/

type ImageVariant = { fileLocation?: string }

export type ExceptionImageEntry = ImageVariant & {
  original?: ImageVariant
  small?: ImageVariant
  medium?: ImageVariant
}

type ExceptionMediaItem = {
  id?: string
  mediaType?: string
  uri?: string
  contentType?: string
}

type ExceptionLike = {
  media?: ExceptionMediaItem[] | null
  images?: ExceptionImageEntry[] | null
  fileLocation?: string
}

export type ExceptionMediaSlide = ExceptionImageEntry & {
  kind: string
  name?: string
  uri?: string
  contentType?: string
  thumbnailUri?: string
}

export type ExceptionImageSize = 'original' | 'small' | 'medium'

export const normalizeFirebaseStoragePath = (path: unknown): string | undefined => {
  if (!path || typeof path !== 'string') return undefined
  if (!path.startsWith('http')) return path
  const match = path.match(FIREBASE_STORAGE_OBJECT_PATH)
  return match ? decodeURIComponent(match[1]) : path
}

/**
 * WO exception images arrive as a storage path, a full Firebase download URL, or nested
 * under images[].{original|small|medium}.fileLocation. Prefer the explicit `photo` entry
 * when rendering a specific slide — callers were passing it but the card always read
 * images[0], duplicating Firebase fetches and showing the same photo on every slide.
 */
export const resolveExceptionImageFileLocation = (
  photo: ExceptionMediaSlide | ExceptionImageEntry | null | undefined,
  exception: unknown,
  size: ExceptionImageSize = 'original'
): string | undefined => {
  const fromPhoto = photo && (photo[size]?.fileLocation || photo.original?.fileLocation || photo.fileLocation)
  const exceptionRecord = (exception ?? {}) as ExceptionLike
  const firstImage = Array.isArray(exceptionRecord.images) ? exceptionRecord.images[0] : undefined
  const raw = fromPhoto || firstImage?.[size]?.fileLocation || firstImage?.original?.fileLocation || exceptionRecord.fileLocation
  return normalizeFirebaseStoragePath(raw)
}

/** Object path a slide renders, whichever of the three shapes it arrived in. */
const resolveSlidePath = (slide: ExceptionMediaSlide): string | undefined =>
  normalizeFirebaseStoragePath(slide.original?.fileLocation || slide.fileLocation || slide.uri)

export const EXCEPTION_MEDIA_KIND = {
  PHOTO: 'photo',
  VIDEO: 'video',
}

/**
 * Unifies the WO-detail `media[]` display join with legacy `images[]` into the slide
 * items the card swiper and gallery render. PHOTO media map onto the legacy photo shape
 * (`original.fileLocation`) so the existing private-bucket blob pipeline keeps working;
 * VIDEO media keep their `uri` for the video player.
 *
 * `media[]` and `images[]` are MERGED, not either/or: work_order_media is Vision-only
 * today (web uploads join it later), while driver photos still arrive on the legacy
 * `images[]`/fileLocation path — an exception can legitimately carry both.
 *
 * Vision emits THUMBNAIL rows alongside VIDEO as posters, not gallery items — they are
 * filtered out of the slides and paired (in order) onto the video slides as
 * `thumbnailUri` for the player to use once real playback lands.
 */
export const getExceptionMediaSlides = (exception: unknown): ExceptionMediaSlide[] => {
  const { media, images, fileLocation } = (exception ?? {}) as ExceptionLike
  const slides: ExceptionMediaSlide[] = []
  const thumbnailUris: Array<string | undefined> = []

  if (Array.isArray(media)) {
    media.forEach((item, index) => {
      const name = item?.id || `media-${index}`
      if (item?.mediaType === 'VIDEO') {
        slides.push({ kind: EXCEPTION_MEDIA_KIND.VIDEO, name, uri: item.uri, contentType: item.contentType })
        return
      }
      if (item?.mediaType === 'THUMBNAIL') {
        thumbnailUris.push(item.uri)
        return
      }
      slides.push({ kind: EXCEPTION_MEDIA_KIND.PHOTO, name, original: { fileLocation: normalizeFirebaseStoragePath(item?.uri) } })
    })
  }

  const videoSlides = slides.filter(({ kind }) => kind === EXCEPTION_MEDIA_KIND.VIDEO)
  thumbnailUris.forEach((uri, index) => {
    if (videoSlides[index]) {
      videoSlides[index].thumbnailUri = normalizeFirebaseStoragePath(uri)
    }
  })

  // Web uploads exist in BOTH sources — a registered media[] row AND the bucket folder the
  // legacy images[] listing scans — so folder entries duplicating registered media are dropped.
  const registeredMediaPaths = new Set(
    slides
      .map(slide => slide.original?.fileLocation || normalizeFirebaseStoragePath(slide.uri))
      .filter((path): path is string => Boolean(path))
  )
  const legacySlides: ExceptionMediaSlide[] = (Array.isArray(images) ? images : [])
    .filter(image => {
      const legacyPath = normalizeFirebaseStoragePath(image?.original?.fileLocation || image?.fileLocation)
      return !legacyPath || !registeredMediaPaths.has(legacyPath)
    })
    .map(image => ({ kind: EXCEPTION_MEDIA_KIND.PHOTO, ...image }))

  const mediaSlides = [...slides, ...legacySlides]

  // A driver photo reaches some payloads only as the exception's own `fileLocation` — the WO-detail
  // join can miss it — and it can sit beside Vision media, so it is appended whenever no slide
  // already renders that path rather than only when both lists came back empty.
  const legacyFileLocation = normalizeFirebaseStoragePath(fileLocation)
  if (!legacyFileLocation || mediaSlides.some(slide => resolveSlidePath(slide) === legacyFileLocation)) return mediaSlides
  return [...mediaSlides, { kind: EXCEPTION_MEDIA_KIND.PHOTO, original: { fileLocation: legacyFileLocation } }]
}

/**
 * Media `uri` values arrive as full download URLs, gs:// URIs (Vision video), or bare
 * object paths in the private uploads bucket. Returns a uri the video player can take
 * (it resolves gs:// via Firebase getDownloadURL itself).
 */
/** Stable React key for a media slide — object paths/uris are unique per upload; `name` collides for same-named files. */
export const getExceptionMediaSlideKey = (image: unknown): string => {
  const slide = (image ?? {}) as Partial<ExceptionMediaSlide>
  const identifier =
    slide.original?.fileLocation || slide.uri || slide.fileLocation || slide.small?.fileLocation || slide.medium?.fileLocation || slide.name
  // Download URLs carry volatile querystring tokens — normalize to the object path so the key stays stable.
  return normalizeFirebaseStoragePath(identifier) || ''
}

export const toPlayableExceptionVideoUri = (uri: unknown): string => {
  if (!uri || typeof uri !== 'string') return ''
  if (uri.startsWith('http') || uri.startsWith('gs://')) return uri
  return `gs://${Constants.IMAGE_UPLOADS_PRIVATE_BUCKET_NAME}/${uri}`
}

const EXCEPTION_IMAGE_FETCH_SIZE: Record<ExceptionImageSize, string> = {
  original: 'default',
  small: 'small',
  medium: 'medium',
}

/**
 * Resolve a variant path from the API, then fetch from the private bucket with suffix
 * fallback to the original (same strategy as ExceptionImageFromPath).
 */
export const loadExceptionImageBase64 = async (
  variantPath: string | undefined,
  basePath: string | undefined,
  size: ExceptionImageSize = 'original'
): Promise<string> => {
  const fetchSize = EXCEPTION_IMAGE_FETCH_SIZE[size] ?? 'default'

  if (variantPath) {
    try {
      const blob = await getExceptionImageBlob(variantPath)
      return await blobToBase64(blob)
    } catch {
      // Variant path from the API may be stale; fall through to suffix/original fetch.
    }
  }

  const path = basePath || variantPath
  if (!path) {
    throw new Error('Exception image path is missing')
  }

  const blob = await getExceptionImageBlobFromPath(path, fetchSize)
  return blobToBase64(blob)
}
